<script setup lang="ts">
import { groupPositionsByTeam, DESCRIPTIONS } from '@/services/careers'

const props = defineProps({
  departmentName: {
    type: String,
    required: true,
  },
  positions: {
    type: Array,
    required: false,
    default: () => [],
  },
  isFilterApplied: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits<{
  (e: 'scrollTop'): void
}>()

const description = computed(
  () => DESCRIPTIONS[props.departmentName]?.description
)

const teams = computed(() => {
  if (!props.positions.length) return []

  const positions = groupPositionsByTeam(props.positions)
  const positionTeams = Object.keys(positions)

  const department = DESCRIPTIONS[props.departmentName]

  const descriptionTeams =
    !props.isFilterApplied && department ? Object.keys(department.teams) : []

  return [...new Set([...positionTeams, ...descriptionTeams])].map((name) => ({
    name,
    positions: positions[name],
    description: department?.teams[name],
  }))
})
</script>

<template>
  <div class="CareersDepartment">
    <h3 class="CareersDepartment_Heading">
      {{ departmentName }}
    </h3>
    <p v-if="description" class="CareersDepartment_Description">
      {{ description }}
    </p>
    <div class="CareersDepartment_Teams">
      <CareersTeam
        v-for="team in teams"
        :key="`department-team-${departmentName}-${team.name}`"
        :team-name="team.name"
        :positions="team.positions"
        :description="team.description"
        :show-title="teams.length > 1"
      />
    </div>
    <p v-if="!teams.length" class="CareersDepartment_NoRoles">
      No open roles at this time.
    </p>
    <SkyButton
      class="CareersDepartment_Return"
      text
      inline
      secondary
      @click="emit('scrollTop')"
    >
      Return to top
    </SkyButton>
  </div>
</template>

<style lang="scss">
.CareersDepartment {
  @include container-medium;

  &_Heading {
    @include type-heading('2');
    margin-top: 0;
    margin-bottom: var(--spacing-4x);
  }

  &_Description {
    max-width: 656px;
  }

  &_Teams {
    display: grid;

    @include for-medium-up {
      grid-template-columns: repeat(3, 1fr);
      gap: var(--spacing-2x);
    }
  }

  &_NoRoles {
    color: var(--text-color-secondary);
    font-family: var(--font-family-default-medium);
    font-weight: var(--font-weight-medium);
    margin: var(--spacing-8x) 0 var(--spacing-4x);
  }

  &_Return {
    --text-color-action-secondary: var(--text-color-action-primary);
    width: max-content;
    margin: 0 auto;
  }
}
</style>
